import { Box, Stack, Text } from '@chakra-ui/react'
import { faSend } from '@fortawesome/pro-regular-svg-icons'
import { useRouter } from 'next/router'
import pMap from 'p-map'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'
import logger from '@/utils/logger'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import useUserPresenceHeartbeat from '@/client/hooks/useUserPresenceHeartbeat'
import RPC from '@/client/rpc'
import type { NewMessageConversationFragment } from '@/client/services/hasura/generated/graphql'
import { EMPTY_RICH_TEXT_VALUE } from '@/client/services/richText/constants'
import isEmpty from '@/client/services/richText/isEmpty'
import type { SerializedRichText } from '@/client/services/richText/types'
import uploadFile from '@/client/services/userUpload/uploadFile'

import Alert from '@/components/UI/Alert'
import RichTextEditorSimple from '@/components/UI/RichTextEditor/RichTextEditorSimple/RichTextEditorSimple'
import type { RichTextEditorSimpleRef } from '@/components/UI/RichTextEditor/RichTextEditorSimple/types'
import type { MentionsProps } from '@/components/UI/RichTextEditor/types'
import type { FileUpload } from '@/components/Worlds/FileUpload/types'
import IconButtonAction from '@/components/Worlds/UI/Button/IconButtonAction'

// NOTE: Do not allow sending a message before uploading an image is complete.
function NewMessageNotMemoized({
  conversation,
  ended = false,
  isAdminView = false,
  onSubmit = noOp,
  onSubmitFailed = noOp,
}: {
  conversation: NewMessageConversationFragment
  ended?: boolean
  isAdminView?: boolean
  onSubmit?: (p: { body: SerializedRichText; clientId: string; attachments: FileUpload[] }) => void
  onSubmitFailed?: (clientId: string) => void
}): JSX.Element {
  const router = useRouter()
  const closed = conversation.closed_at != null && new Date(conversation.closed_at) < new Date()

  const editorRef = useRef<RichTextEditorSimpleRef>(null)

  const [body, setBody] = useState<SerializedRichText>(EMPTY_RICH_TEXT_VALUE)
  const [blockedError, setBlockedError] = useState<string | null>(null)
  const heartbeat = useUserPresenceHeartbeat({ conversationId: conversation.id }, 2500)

  const isBodyEmpty = useMemo(() => isEmpty(body), [body])

  const chatHasClosed = ended || closed

  const handleChange = useCallback(
    (newBody: SerializedRichText) => {
      setBody(newBody)
      heartbeat()
    },
    [heartbeat]
  )

  const [handleSendMessage, isSendingMessage] = useAPIWrapper(
    async () => {
      if (chatHasClosed || isBodyEmpty) {
        return
      }

      const uploads = editorRef.current?.getUploads() ?? []
      const shouldOptimisticallyUpdate = uploads.length === 0

      setBlockedError(null)

      if (shouldOptimisticallyUpdate) {
        editorRef.current?.clear()
        editorRef.current?.setValue(EMPTY_RICH_TEXT_VALUE)
        setBody(EMPTY_RICH_TEXT_VALUE)
      }

      const clientId = uuidv4()

      if (shouldOptimisticallyUpdate) {
        const optimisticAttachments = uploads.map(item => ({
          loading: true,
          src: URL.createObjectURL(item),
          file: item,
        }))

        onSubmit({
          body,
          clientId,
          attachments: optimisticAttachments,
        })
      }

      const attachmentUrls = await pMap(
        uploads,
        async file => {
          const { downloadUrl } = await uploadFile({
            file,
            fileType: file.type,
            target: { type: 'attachment' },
          })
          return downloadUrl
        },
        { concurrency: 3 }
      )

      try {
        const sendMessageResult = await RPC.sendMessage(
          {
            conversationId: conversation.id,
            body,
            attachmentUrls,
            clientId,
          },
          {
            disableProgressBar: true,
          }
        )

        if (sendMessageResult.status === 'blocked') {
          onSubmitFailed(clientId)
          setBlockedError(sendMessageResult.message)
          editorRef.current?.setValue(body)
          setBody(body)
          return
        }

        if (sendMessageResult.status === 'suspended') {
          router.reload()
          return
        }

        if (!shouldOptimisticallyUpdate) {
          editorRef.current?.clear()
          editorRef.current?.setValue(EMPTY_RICH_TEXT_VALUE)
          setBody(EMPTY_RICH_TEXT_VALUE)
        }
      } catch (error) {
        logger.warning('Error posting comment', { error })
        onSubmitFailed(clientId)
        throw error
      }
    },
    [onSubmit, body, conversation.id, onSubmitFailed, router, chatHasClosed, isBodyEmpty],
    'Send Message'
  )

  const mentions: MentionsProps = useMemo(
    () => ({
      usersInThread: conversation.conversation_users.map(user => ({
        id: user.user.id,
        displayName: user.user.virtual_display_name,
        avatarUrl: user.user.virtual_avatar_url,
        username: user.user.username,
      })),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conversation.conversation_users.map(item => item.user.id).join('-')]
  )

  useEffect(() => {
    if (editorRef.current != null && !chatHasClosed && !isSendingMessage) {
      editorRef.current.focus()
    }
  }, [editorRef, chatHasClosed, isSendingMessage, conversation.id])

  return (
    <Stack>
      {blockedError == null ? null : (
        <Box px="12px">
          <Alert variant="error">
            <Text>{blockedError}</Text>
          </Alert>
        </Box>
      )}
      <RichTextEditorSimple
        ref={editorRef}
        name="NewMessage"
        placeholder={
          chatHasClosed
            ? 'This chat has been closed.'
            : isAdminView
              ? 'Using admin privileges to send message'
              : 'Reply...'
        }
        initialValue={EMPTY_RICH_TEXT_VALUE}
        mentions={mentions}
        onEnter={handleSendMessage}
        onChange={handleChange}
        disableNewLineOnEnter
        hideListFormatting
        disabled={chatHasClosed || isSendingMessage}
        rightFooterContent={() => (
          <IconButtonAction
            label="Send message"
            faIcon={faSend}
            onClick={handleSendMessage}
            isDisabled={chatHasClosed || isBodyEmpty || isSendingMessage}
          />
        )}
        containerProps={{
          borderRadius: '0px',
          border: 'none',
          borderTop: `2px solid ${chakraTokenToVar('border.muted')}`,
        }}
        showMicrophone
      />
    </Stack>
  )
}

// Memoize this, so that changing one input doesn't affect the rendering
// of all the other inputs.
const NewMessage = memo(
  NewMessageNotMemoized,
  (prevProps, newProps) => prevProps.conversation?.id === newProps.conversation?.id
)

export default NewMessage
